import { jsx, jsxs } from "react/jsx-runtime";
import assert from "assert";
import dedent from "dedent";
import { createContext, forwardRef, useContext } from "react";
import { ensureResetImported } from "../../css/reset/resetTracker.mjs";
import { useHideFocusRings } from "../private/hideFocusRings/useHideFocusRings.mjs";
import { DefaultMessagesContext } from "../private/DefaultMessagesContext.mjs";
import { ThemeTestProviderContext } from "../ThemeTestProvider/ThemeTestProviderContext.mjs";
import { BreakpointProvider } from "./BreakpointContext.mjs";
import { ThemeContext } from "./ThemeContext.mjs";
import { darkMode } from "../../css/atoms/sprinkles.css.mjs";
import { VanillaThemeContainer } from "./VanillaThemeContainer.mjs";
if (process.env.NODE_ENV === "development") {
  ensureResetImported();
}
const makeLinkComponent = (render) => ({ __forwardRef__: forwardRef(render) });
const DefaultLinkComponent = makeLinkComponent((props, ref) => /* @__PURE__ */ jsx("a", { ref, ...props }));
const LinkComponentContext = createContext(DefaultLinkComponent);
const useLinkComponent = (ref) => {
  const linkComponent = useContext(LinkComponentContext);
  assert(
    !ref || "__forwardRef__" in linkComponent,
    dedent`
      You're passing a ref to a Theme link, but your app is providing a custom link component to 'ThemeProvider' that doesn't appear to support refs.

      To fix this, you need to use Theme's 'makeLinkComponent' helper function when creating your custom link component. This ensures that refs are forwarded correctly, and allows us to silence this error message.

      For more information and an example implementation, check out the documentation for 'ThemeProvider': https://seek-oss.github.io/Theme-design-system/components/ThemeProvider
    `
  );
  if ("__forwardRef__" in linkComponent) {
    return linkComponent.__forwardRef__;
  }
  return linkComponent;
};
const ThemeProvider = ({
  theme,
  styleBody = true,
  linkComponent,
  children,
  options
}) => {
  const alreadyInProvider = Boolean(useContext(ThemeContext));
  const inTestProvider = useContext(ThemeTestProviderContext);
  const linkComponentFromContext = useContext(LinkComponentContext);
  useHideFocusRings(!alreadyInProvider || inTestProvider);
  assert(
    inTestProvider || typeof navigator === "undefined" || navigator.userAgent === void 0 || navigator.userAgent.indexOf("jsdom") === -1,
    `Rendering 'ThemeProvider' in Jest is not supported as it expects a browser environment. Please switch to 'ThemeTestProvider'. See the docs for more info: https://seek-oss.github.io/Theme-design-system/components/ThemeTestProvider`
  );
  return /* @__PURE__ */ jsxs(ThemeContext.Provider, { value: theme, children: [
    styleBody ? /* @__PURE__ */ jsx("style", { type: "text/css", children: `
            html,body{margin:0;padding:0;background:${theme.background.lightMode}}
            html.${darkMode},html.${darkMode} body{color-scheme:dark;background:${theme.background.darkMode}}
          ` }) : null,
    /* @__PURE__ */ jsx(VanillaThemeContainer, { theme: theme.vanillaTheme, setDefaultTextTones: !alreadyInProvider, children: /* @__PURE__ */ jsx(DefaultMessagesContext.Provider, { value: options, children: /* @__PURE__ */ jsx(LinkComponentContext.Provider, { value: linkComponent || linkComponentFromContext, children: alreadyInProvider || inTestProvider ? children : /* @__PURE__ */ jsx(BreakpointProvider, { children }) }) }) })
  ] });
};
export {
  ThemeProvider,
  makeLinkComponent,
  useLinkComponent
};
